// ==========================================================================
// BottomSheet Component - Slide up modal from bottom
// Supports swipe-to-close gesture (Android-style)
// ==========================================================================

import { useEffect, useRef, useState, useCallback } from 'react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  showOverlay?: boolean;
}

const CLOSE_THRESHOLD = 100; // Pixels to drag before closing
const VELOCITY_THRESHOLD = 0.5; // Pixels per ms to trigger close

export function BottomSheet({ isOpen, onClose, children, showOverlay = true }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // Touch tracking refs
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const currentY = useRef(0);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setDragOffset(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    currentY.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - touchStartY.current;
    
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      setDragOffset(diff);
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    const diff = currentY.current - touchStartY.current;
    const timeDiff = Date.now() - touchStartTime.current;
    const velocity = diff / timeDiff; // pixels per ms
    
    // Close if dragged past threshold or with enough velocity
    if (diff > CLOSE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      // Animate out before closing
      setDragOffset(window.innerHeight);
      setTimeout(() => {
        onClose();
        setDragOffset(0);
      }, 200);
    } else {
      // Snap back
      setDragOffset(0);
    }
    
    setIsDragging(false);
  }, [isDragging, onClose]);

  // Handle mouse events for desktop testing
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    touchStartY.current = e.clientY;
    touchStartTime.current = Date.now();
    currentY.current = e.clientY;
    setIsDragging(true);
    
    const handleMouseMove = (e: MouseEvent) => {
      currentY.current = e.clientY;
      const diff = currentY.current - touchStartY.current;
      if (diff > 0) {
        setDragOffset(diff);
      }
    };
    
    const handleMouseUp = () => {
      const diff = currentY.current - touchStartY.current;
      const timeDiff = Date.now() - touchStartTime.current;
      const velocity = diff / timeDiff;
      
      if (diff > CLOSE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
        setDragOffset(window.innerHeight);
        setTimeout(() => {
          onClose();
          setDragOffset(0);
        }, 200);
      } else {
        setDragOffset(0);
      }
      
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onClose]);

  if (!isOpen) return null;

  const sheetStyle = {
    transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      {showOverlay && (
        <div 
          className="absolute inset-0 bg-black/40 transition-opacity"
          style={{ opacity: Math.max(0, 1 - dragOffset / 300) }}
          onClick={onClose}
        />
      )}
      
      {/* Sheet */}
      <div 
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[24px] shadow-xl animate-slide-up"
        style={sheetStyle}
      >
        {/* Drag Handle - main swipe area */}
        <div 
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        
        {/* Extended drag area for easier grabbing */}
        <div 
          className="h-4 -mt-2 touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        />
        
        {/* Content */}
        <div className="px-6 pb-8">
          {children}
        </div>
      </div>
    </div>
  );
}
